import React, { useState, useEffect } from "react";
import axiosInstance from "../../../../axios";
import { Loading } from "../../../Other/Loading";
import { useSelector } from "react-redux";
import Modal from "../../../Other/Modal";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBook,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaInfoCircle,
  FaList,
  FaTh,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
} from "react-icons/fa";
import moment from "moment";

const StudentCourseAttendance = () => {
  const { user } = useSelector((state) => state.users);
  const studentId = user?.id;

  const [courses, setCourses] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Layout View Mode: "normal" (list) or "grid"
  const [viewMode, setViewMode] = useState("normal");

  // Expanded courses in accordion (for list view)
  const [expandedCourses, setExpandedCourses] = useState({});

  // Course List Pagination
  const [coursesCurrentPage, setCoursesCurrentPage] = useState(1);
  const coursesPerPage = 4;

  // Attendance Records Pagination (per course key)
  const [recordsPages, setRecordsPages] = useState({});
  const recordsPerPage = 5;

  // Modal details state (for grid view)
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeModalGroup, setActiveModalGroup] = useState(null);

  // Filters state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (!studentId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const [coursesRes, attendanceRes] = await Promise.all([
          axiosInstance.get("/course/purchased"),
          axiosInstance.get("/student/attendance", { params: { studentId } }),
        ]);

        setCourses(Array.isArray(coursesRes?.data?.data?.data) ? coursesRes.data.data.data : []);
        setAttendance(Array.isArray(attendanceRes?.data?.data) ? attendanceRes.data.data : []);
      } catch (err) {
        console.error("Error fetching attendance details:", err);
        setError("Failed to fetch attendance history or course listings.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studentId]);

  // Reset page when filters change
  useEffect(() => {
    setCoursesCurrentPage(1);
    setRecordsPages({});
  }, [searchQuery, statusFilter, startDate, endDate]);

  // Group attendance records by course
  const groupedAttendance = React.useMemo(() => {
    const groups = {};

    // Pre-initialize groups for all enrolled courses
    courses.forEach((course) => {
      groups[course.ID] = {
        courseId: course.ID,
        courseName: course.CourseName,
        records: [],
      };
    });

    // Group attendance records
    attendance.forEach((record) => {
      // Apply status and date filters first
      if (statusFilter !== "all" && record.status?.toLowerCase() !== statusFilter.toLowerCase()) {
        return;
      }
      if (startDate) {
        const recordDateStr = moment(record.date).format("YYYY-MM-DD");
        if (recordDateStr < startDate) return;
      }
      if (endDate) {
        const recordDateStr = moment(record.date).format("YYYY-MM-DD");
        if (recordDateStr > endDate) return;
      }

      // Identify corresponding course
      let targetCourseId = record.courseId;

      // Fallback matching if courseId is null but courseName matches
      if (!targetCourseId && record.courseName) {
        const matched = courses.find(
          (c) => c.CourseName?.toLowerCase().trim() === record.courseName?.toLowerCase().trim()
        );
        if (matched) {
          targetCourseId = matched.ID;
        }
      }

      if (targetCourseId && groups[targetCourseId]) {
        groups[targetCourseId].records.push(record);
      } else {
        const fallbackKey = "unassigned";
        if (!groups[fallbackKey]) {
          groups[fallbackKey] = {
            courseId: null,
            courseName: "General / Unassigned Attendance",
            records: [],
          };
        }
        groups[fallbackKey].records.push(record);
      }
    });

    return groups;
  }, [courses, attendance, statusFilter, startDate, endDate]);

  // Filter groups by course search and hide empty ones when filter is active
  const filteredCourseGroups = React.useMemo(() => {
    return Object.values(groupedAttendance).filter((group) => {
      if (searchQuery.trim() && !group.courseName?.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      const hasFiltersActive = statusFilter !== "all" || startDate || endDate;
      if (hasFiltersActive && group.records.length === 0) {
        return false;
      }
      return true;
    });
  }, [groupedAttendance, searchQuery, statusFilter, startDate, endDate]);

  // Course level pagination calculations
  const totalCoursePages = Math.ceil(filteredCourseGroups.length / coursesPerPage);
  const paginatedCourseGroups = React.useMemo(() => {
    const start = (coursesCurrentPage - 1) * coursesPerPage;
    return filteredCourseGroups.slice(start, start + coursesPerPage);
  }, [filteredCourseGroups, coursesCurrentPage, coursesPerPage]);

  // Toggle accordion item
  const toggleCourseExpand = (courseId) => {
    const key = courseId || "unassigned";
    setExpandedCourses((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Filtered attendance records for stats (only by date range)
  const filteredAttendanceForStats = React.useMemo(() => {
    return attendance.filter((record) => {
      if (startDate) {
        const recordDateStr = moment(record.date).format("YYYY-MM-DD");
        if (recordDateStr < startDate) return false;
      }
      if (endDate) {
        const recordDateStr = moment(record.date).format("YYYY-MM-DD");
        if (recordDateStr > endDate) return false;
      }
      return true;
    });
  }, [attendance, startDate, endDate]);

  // Overall Statistics
  const stats = React.useMemo(() => {
    const totalMarked = filteredAttendanceForStats.length;
    const presentCount = filteredAttendanceForStats.filter((r) => r.status?.toLowerCase() === "present").length;
    const absentCount = filteredAttendanceForStats.filter((r) => r.status?.toLowerCase() === "absent").length;
    const pendingCount = filteredAttendanceForStats.filter((r) => r.status?.toLowerCase() === "pending").length;
    const rate = totalMarked > 0 ? ((presentCount / totalMarked) * 100).toFixed(0) : 0;

    return {
      totalMarked,
      presentCount,
      absentCount,
      pendingCount,
      rate,
    };
  }, [filteredAttendanceForStats]);

  // Records pagination helpers
  const getRecordPage = (courseKey) => recordsPages[courseKey] || 1;

  const setRecordPageValue = (courseKey, page) => {
    setRecordsPages((prev) => ({
      ...prev,
      [courseKey]: page,
    }));
  };

  const getPaginatedRecords = (group) => {
    const courseKey = group.courseId || "unassigned";
    const currentPage = getRecordPage(courseKey);
    const start = (currentPage - 1) * recordsPerPage;
    return group.records.slice(start, start + recordsPerPage);
  };

  // Open modal details (for Grid view)
  const handleOpenGridDetails = (group) => {
    setActiveModalGroup(group);
    setShowDetailsModal(true);
  };

  // Handle page resets inside active modal when closed
  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setActiveModalGroup(null);
  };

  // Sub-component to render attendance table with pagination inside accordion/modal
  const AttendanceTableWithPagination = ({ group }) => {
    const courseKey = group.courseId || "unassigned";
    const currentPage = getRecordPage(courseKey);
    const recordsCount = group.records.length;
    const totalPages = Math.ceil(recordsCount / recordsPerPage);
    const paginatedRecords = getPaginatedRecords(group);

    if (recordsCount === 0) {
      return (
        <p style={{ margin: 0, padding: "16px", color: "#64748b", fontSize: "13px", fontWeight: "600", fontStyle: "italic", textAlign: "center" }}>
          No check-in attendance records found for this course.
        </p>
      );
    }

    return (
      <div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                <th style={{ padding: "12px 14px", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>
                  Date
                </th>
                <th style={{ padding: "12px 14px", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>
                  Check-In Time
                </th>
                <th style={{ padding: "12px 14px", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", textAlign: "center" }}>
                  Status
                </th>
                <th style={{ padding: "12px 14px", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", textAlign: "center" }}>
                  Verification
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedRecords.map((record, index) => {
                const isPresent = record.status?.toLowerCase() === "present";
                const isAbsent = record.status?.toLowerCase() === "absent";

                let statusColor = "#d97706";
                let statusBg = "#fffbeb";
                if (isPresent) {
                  statusColor = "#16a34a";
                  statusBg = "#f0fdf4";
                } else if (isAbsent) {
                  statusColor = "#dc2626";
                  statusBg = "#fef2f2";
                }

                return (
                  <tr
                    key={record.id || index}
                    style={{
                      borderBottom: "1px solid #f1f5f9",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#fafafa";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {/* Date */}
                    <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: "700", color: "#1e293b" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <FaCalendarAlt style={{ color: "#94a3b8", fontSize: "12px" }} />
                        {moment(record.date).format("MMM DD, YYYY")}
                      </span>
                    </td>

                    {/* Time */}
                    <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <FaClock style={{ color: "#94a3b8", fontSize: "12px" }} />
                        {record.checkInTime || "N/A"}
                      </span>
                    </td>

                    {/* Status */}
                    <td style={{ padding: "12px 14px", textAlign: "center" }}>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "11px",
                          fontWeight: "700",
                          background: statusBg,
                          color: statusColor,
                          display: "inline-block",
                        }}
                      >
                        {record.status || "Pending"}
                      </span>
                    </td>

                    {/* Verification */}
                    <td style={{ padding: "12px 14px", textAlign: "center" }}>
                      {record.verified === 1 ? (
                        <span style={{ color: "#16a34a", fontSize: "12px", fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <FaCheckCircle /> Verified
                        </span>
                      ) : (
                        <span style={{ color: "#dc2626", fontSize: "12px", fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <FaTimesCircle /> Not Verified
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Attendance Log Table Pagination Controls */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "16px",
              paddingTop: "12px",
              borderTop: "1px solid #f1f5f9",
            }}
          >
            <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "600" }}>
              Showing {Math.min(recordsCount, (currentPage - 1) * recordsPerPage + 1)}-
              {Math.min(recordsCount, currentPage * recordsPerPage)} of {recordsCount} logs
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setRecordPageValue(courseKey, currentPage - 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "28px",
                  height: "28px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: "white",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  opacity: currentPage === 1 ? 0.5 : 1,
                  color: "#475569",
                }}
              >
                <FaChevronLeft style={{ fontSize: "10px" }} />
              </button>
              <span style={{ fontSize: "13px", color: "#334155", fontWeight: "700", alignSelf: "center", padding: "0 6px" }}>
                {currentPage} / {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setRecordPageValue(courseKey, currentPage + 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "28px",
                  height: "28px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  background: "white",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  opacity: currentPage === totalPages ? 0.5 : 1,
                  color: "#475569",
                }}
              >
                <FaChevronRight style={{ fontSize: "10px" }} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "24px", color: "#dc2626", fontWeight: "700" }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Outfit', sans-serif", padding: "10px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#f8fafc",
          padding: "16px 24px",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          marginBottom: "24px",
        }}
      >
        <p style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: "#0f172a" }}>
          My Attendance Summary
        </p>
        <span
          style={{
            fontSize: "12px",
            background: "#eff6ff",
            color: "#2563eb",
            fontWeight: "700",
            padding: "6px 12px",
            borderRadius: "20px",
            border: "1px solid #dbeafe",
          }}
        >
          Student Dashboard
        </span>
      </div>

      {/* Stats Cards Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "18px",
          marginBottom: "30px",
        }}
      >
        {/* Attendance Rate */}
        <div style={{ background: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)", color: "white", padding: "20px", borderRadius: "20px", boxShadow: "0 4px 15px rgba(79, 70, 229, 0.15)" }}>
          <div style={{ fontSize: "12px", fontWeight: "700", opacity: 0.85, textTransform: "uppercase" }}>
            Overall Attendance Rate
          </div>
          <div style={{ fontSize: "28px", fontWeight: "900", marginTop: "8px" }}>{stats.rate}%</div>
          <div style={{ fontSize: "11px", marginTop: "6px", opacity: 0.8 }}>Based on {stats.totalMarked} total check-ins</div>
        </div>

        {/* Present Days */}
        <div style={{ background: "white", padding: "20px", borderRadius: "20px", border: "1px solid #e2e8f0", boxShadow: "0 4px 15px rgba(0, 0, 0, 0.01)" }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Present Count</div>
          <div style={{ fontSize: "28px", fontWeight: "900", color: "#16a34a", marginTop: "8px" }}>{stats.presentCount}</div>
          <div style={{ fontSize: "11px", color: "#16a34a", fontWeight: "600", marginTop: "6px" }}>Successfully Verified</div>
        </div>

        {/* Absent Days */}
        <div style={{ background: "white", padding: "20px", borderRadius: "20px", border: "1px solid #e2e8f0", boxShadow: "0 4px 15px rgba(0, 0, 0, 0.01)" }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Absent Count</div>
          <div style={{ fontSize: "28px", fontWeight: "900", color: "#dc2626", marginTop: "8px" }}>{stats.absentCount}</div>
          <div style={{ fontSize: "11px", color: "#dc2626", fontWeight: "600", marginTop: "6px" }}>Unverified / Missed</div>
        </div>

        {/* Pending Verifications */}
        <div style={{ background: "white", padding: "20px", borderRadius: "20px", border: "1px solid #e2e8f0", boxShadow: "0 4px 15px rgba(0, 0, 0, 0.01)" }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Pending Verification</div>
          <div style={{ fontSize: "28px", fontWeight: "900", color: "#d97706", marginTop: "8px" }}>{stats.pendingCount}</div>
          <div style={{ fontSize: "11px", color: "#d97706", fontWeight: "600", marginTop: "6px" }}>Awaiting Instructor Action</div>
        </div>
      </div>

      {/* Filter and View Layout Toolbar */}
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          padding: "20px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.01)",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Search bar */}
          <div style={{ position: "relative", minWidth: "260px", flex: 1 }}>
            <input
              type="text"
              placeholder="Search course attendance..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px 10px 36px",
                borderRadius: "12px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                fontWeight: "600",
                color: "#334155",
                outline: "none",
              }}
            />
            <FaSearch style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "14px" }} />
          </div>

          {/* Controls */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
            {/* View Mode Toggle */}
            <div style={{ display: "flex", background: "#f1f5f9", padding: "4px", borderRadius: "10px", marginRight: "8px" }}>
              <button
                onClick={() => setViewMode("normal")}
                title="Normal (List) View"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "8px",
                  background: viewMode === "normal" ? "white" : "transparent",
                  color: viewMode === "normal" ? "#0f172a" : "#64748b",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  boxShadow: viewMode === "normal" ? "0 2px 5px rgba(0,0,0,0.08)" : "none",
                  transition: "all 0.2s ease",
                }}
              >
                <FaList /> Normal
              </button>
              <button
                onClick={() => setViewMode("grid")}
                title="Grid View"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "8px",
                  background: viewMode === "grid" ? "white" : "transparent",
                  color: viewMode === "grid" ? "#0f172a" : "#64748b",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  boxShadow: viewMode === "grid" ? "0 2px 5px rgba(0,0,0,0.08)" : "none",
                  transition: "all 0.2s ease",
                }}
              >
                <FaTh /> Grid
              </button>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: "10px 14px",
                borderRadius: "12px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                fontWeight: "600",
                background: "white",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <option value="all">All Statuses</option>
              <option value="present">Present</option>
              <option value="absent">Absent</option>
              <option value="pending">Pending</option>
            </select>

            {/* Date Filters */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ padding: "9px 12px", borderRadius: "12px", border: "1px solid #cbd5e1", fontSize: "13px", fontWeight: "600", outline: "none" }}
              />
              <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "700" }}>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ padding: "9px 12px", borderRadius: "12px", border: "1px solid #cbd5e1", fontSize: "13px", fontWeight: "600", outline: "none" }}
              />
            </div>

            {/* Clear button */}
            {(searchQuery || statusFilter !== "all" || startDate || endDate) && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("all");
                  setStartDate("");
                  setEndDate("");
                }}
                style={{
                  background: "#f1f5f9",
                  color: "#475569",
                  border: "none",
                  padding: "10px 16px",
                  borderRadius: "12px",
                  fontSize: "13px",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Course Layout rendering */}
      {filteredCourseGroups.length === 0 ? (
        <div style={{ padding: "40px", textAlign: "center", background: "white", borderRadius: "20px", border: "1px dashed #cbd5e1", color: "#64748b" }}>
          <FaInfoCircle style={{ fontSize: "24px", color: "#94a3b8", marginBottom: "8px" }} />
          <p style={{ margin: 0, fontWeight: "600", fontSize: "14px" }}>
            No attendance records found matching filters.
          </p>
        </div>
      ) : (
        <div>
          {/* 1. Normal View (List / Accordions) */}
          {viewMode === "normal" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {paginatedCourseGroups.map((group) => {
                const courseKey = group.courseId || "unassigned";
                const isExpanded = !!expandedCourses[courseKey];
                const recordsCount = group.records.length;

                const presents = group.records.filter((r) => r.status?.toLowerCase() === "present").length;
                const absents = group.records.filter((r) => r.status?.toLowerCase() === "absent").length;
                const pendings = group.records.filter((r) => r.status?.toLowerCase() === "pending").length;
                const courseRate = recordsCount > 0 ? ((presents / recordsCount) * 100).toFixed(0) : 0;

                return (
                  <div key={courseKey} style={{ background: "white", borderRadius: "20px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.01)", overflow: "hidden" }}>
                    <div
                      onClick={() => toggleCourseExpand(group.courseId)}
                      style={{
                        padding: "20px 24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        background: isExpanded ? "#f8fafc" : "transparent",
                        borderBottom: isExpanded ? "1px solid #e2e8f0" : "none",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0, paddingRight: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <FaBook style={{ color: "#4f46e5", fontSize: "15px", flexShrink: 0 }} />
                          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "800", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {group.courseName}
                          </h3>
                        </div>
                        <div style={{ display: "flex", gap: "10px", marginTop: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b" }}>Total Classes: {recordsCount}</span>
                          <span style={{ fontSize: "11px", fontWeight: "700", color: "#16a34a" }}>Present: {presents}</span>
                          {absents > 0 && <span style={{ fontSize: "11px", fontWeight: "700", color: "#dc2626" }}>Absent: {absents}</span>}
                          {pendings > 0 && <span style={{ fontSize: "11px", fontWeight: "700", color: "#d97706" }}>Pending: {pendings}</span>}
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "20px", flexShrink: 0 }}>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Attendance Rate</div>
                          <div style={{ fontSize: "18px", fontWeight: "900", color: Number(courseRate) >= 75 ? "#16a34a" : Number(courseRate) >= 50 ? "#d97706" : "#dc2626", marginTop: "2px" }}>
                            {courseRate}%
                          </div>
                        </div>
                        <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", color: "#475569" }}>
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={{ padding: "20px 24px", background: "#ffffff" }}>
                        <AttendanceTableWithPagination group={group} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 2. Grid View Layout */}
          {viewMode === "grid" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
              {paginatedCourseGroups.map((group) => {
                const courseKey = group.courseId || "unassigned";
                const recordsCount = group.records.length;

                const presents = group.records.filter((r) => r.status?.toLowerCase() === "present").length;
                const absents = group.records.filter((r) => r.status?.toLowerCase() === "absent").length;
                const pendings = group.records.filter((r) => r.status?.toLowerCase() === "pending").length;
                const courseRate = recordsCount > 0 ? ((presents / recordsCount) * 100).toFixed(0) : 0;

                let rateColor = "#dc2626";
                let rateBg = "#fef2f2";
                if (Number(courseRate) >= 75) {
                  rateColor = "#16a34a";
                  rateBg = "#f0fdf4";
                } else if (Number(courseRate) >= 50) {
                  rateColor = "#d97706";
                  rateBg = "#fffbeb";
                }

                return (
                  <div
                    key={courseKey}
                    style={{
                      background: "white",
                      borderRadius: "20px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.02)",
                      padding: "20px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "transform 0.2s, box-shadow 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.02)";
                    }}
                  >
                    <div>
                      {/* Title block */}
                      <div style={{ display: "flex", alignItems: "start", gap: "10px", marginBottom: "14px" }}>
                        <div style={{ background: "#eff6ff", padding: "8px", borderRadius: "10px", color: "#2563eb", marginTop: "2px" }}>
                          <FaBook style={{ fontSize: "14px" }} />
                        </div>
                        <h4 style={{ margin: 0, fontSize: "15px", fontWeight: "800", color: "#0f172a", lineHeight: "1.4" }}>
                          {group.courseName}
                        </h4>
                      </div>

                      {/* Horizontal progress bar */}
                      <div style={{ marginBottom: "16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                          <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Attendance rate</span>
                          <span style={{ fontSize: "12px", fontWeight: "800", color: rateColor }}>{courseRate}%</span>
                        </div>
                        <div style={{ width: "100%", height: "6px", background: "#f1f5f9", borderRadius: "10px", overflow: "hidden" }}>
                          <div style={{ width: `${courseRate}%`, height: "100%", background: rateColor, borderRadius: "10px" }} />
                        </div>
                      </div>

                      {/* Stats grid */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginBottom: "18px", background: "#f8fafc", padding: "10px", borderRadius: "12px" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "10px", color: "#64748b", fontWeight: "700" }}>TOTAL</div>
                          <div style={{ fontSize: "14px", fontWeight: "800", color: "#334155", marginTop: "2px" }}>{recordsCount}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "10px", color: "#16a34a", fontWeight: "700" }}>PRESENT</div>
                          <div style={{ fontSize: "14px", fontWeight: "800", color: "#16a34a", marginTop: "2px" }}>{presents}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "10px", color: "#dc2626", fontWeight: "700" }}>ABSENT</div>
                          <div style={{ fontSize: "14px", fontWeight: "800", color: "#dc2626", marginTop: "2px" }}>{absents}</div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleOpenGridDetails(group)}
                      style={{
                        width: "100%",
                        background: "#f1f5f9",
                        color: "#2563eb",
                        border: "none",
                        padding: "10px",
                        borderRadius: "12px",
                        fontWeight: "800",
                        fontSize: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        transition: "background 0.2s, color 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#eff6ff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#f1f5f9";
                      }}
                    >
                      View Logs <FaExternalLinkAlt style={{ fontSize: "10px" }} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Course-level Pagination Footer Controls */}
          {totalCoursePages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "12px",
                marginTop: "30px",
                paddingTop: "20px",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <button
                disabled={coursesCurrentPage === 1}
                onClick={() => setCoursesCurrentPage((p) => p - 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 16px",
                  borderRadius: "12px",
                  border: "1px solid #cbd5e1",
                  background: "white",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#475569",
                  cursor: coursesCurrentPage === 1 ? "not-allowed" : "pointer",
                  opacity: coursesCurrentPage === 1 ? 0.5 : 1,
                  transition: "background 0.2s",
                }}
              >
                <FaChevronLeft style={{ fontSize: "10px" }} /> Previous
              </button>

              <span style={{ fontSize: "13px", color: "#0f172a", fontWeight: "800" }}>
                Page {coursesCurrentPage} of {totalCoursePages}
              </span>

              <button
                disabled={coursesCurrentPage === totalCoursePages}
                onClick={() => setCoursesCurrentPage((p) => p + 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 16px",
                  borderRadius: "12px",
                  border: "1px solid #cbd5e1",
                  background: "white",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#475569",
                  cursor: coursesCurrentPage === totalCoursePages ? "not-allowed" : "pointer",
                  opacity: coursesCurrentPage === totalCoursePages ? 0.5 : 1,
                  transition: "background 0.2s",
                }}
              >
                Next <FaChevronRight style={{ fontSize: "10px" }} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Grid View Details Modal */}
      {showDetailsModal && activeModalGroup && (
        <Modal isOpen={showDetailsModal} onClose={handleCloseModal}>
          <div style={{ fontFamily: "'Outfit', sans-serif" }}>
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e2e8f0",
                paddingBottom: "16px",
                marginBottom: "20px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ background: "#eff6ff", padding: "8px", borderRadius: "10px", color: "#2563eb" }}>
                  <FaBook style={{ fontSize: "16px" }} />
                </div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "900", color: "#0f172a" }}>
                  {activeModalGroup.courseName}
                </h3>
              </div>
            </div>

            {/* Modal Body: Attendance Logs */}
            <div style={{ padding: "4px 0" }}>
              <div style={{ display: "flex", gap: "10px", marginBottom: "20px", background: "#f8fafc", padding: "12px 16px", borderRadius: "14px" }}>
                <span style={{ fontSize: "12px", fontWeight: "700", color: "#475569" }}>
                  Total Marked: {activeModalGroup.records.length}
                </span>
                <span style={{ fontSize: "12px", fontWeight: "700", color: "#16a34a" }}>
                  Present: {activeModalGroup.records.filter((r) => r.status?.toLowerCase() === "present").length}
                </span>
                <span style={{ fontSize: "12px", fontWeight: "700", color: "#dc2626" }}>
                  Absent: {activeModalGroup.records.filter((r) => r.status?.toLowerCase() === "absent").length}
                </span>
              </div>

              <AttendanceTableWithPagination group={activeModalGroup} />
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "24px",
                paddingTop: "16px",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <button
                onClick={handleCloseModal}
                style={{
                  background: "#4f46e5",
                  color: "white",
                  border: "none",
                  padding: "10px 24px",
                  borderRadius: "12px",
                  fontWeight: "800",
                  fontSize: "13px",
                  cursor: "pointer",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#3730a3";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#4f46e5";
                }}
              >
                Close Logs
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default StudentCourseAttendance;
